import { useState, useEffect } from 'react'
import { DashboardLayout } from '../../components/layout'
import { Button, Card } from '../../components/ui'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import { getSupabase } from '../../lib/supabase'
import { isMicrosoftConfigured, getAuthUrl } from '../../lib/microsoft'

function SettingsPage() {
  const { user, profile } = useAuth()
  const toast = useToast()

  const [microsoftConnected, setMicrosoftConnected] = useState(false)
  const [microsoftEmail, setMicrosoftEmail] = useState(null)
  const [calendarSyncEnabled, setCalendarSyncEnabled] = useState(false)
  const [todoSyncEnabled, setTodoSyncEnabled] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (profile) {
      setMicrosoftConnected(profile.microsoft_connected || false)
      setMicrosoftEmail(profile.microsoft_email || null)
      setCalendarSyncEnabled(profile.calendar_sync_enabled || false)
      setTodoSyncEnabled(profile.todo_sync_enabled || false)
    }
  }, [profile])

  // Check for OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')

    if (code && user) {
      handleMicrosoftCallback(code)
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }, [user])

  const handleMicrosoftCallback = async (code) => {
    setLoading(true)
    const client = getSupabase()

    try {
      // Exchange code for token via Edge Function
      const { data, error } = await client.functions.invoke('microsoft-auth', {
        body: {
          action: 'exchange',
          code,
          redirectUri: `${window.location.origin}/dashboard/settings`,
          userId: user.id,
        },
      })

      if (error) throw error

      // Update profile
      await client.from('profiles').update({
        microsoft_connected: true,
        microsoft_email: data.email || 'Connected',
      }).eq('id', user.id)

      setMicrosoftConnected(true)
      toast.success('Microsoft 365 connected successfully!')

    } catch (err) {
      console.error('Microsoft auth error:', err)
      toast.error('Failed to connect Microsoft 365')
    }

    setLoading(false)
  }

  const connectMicrosoft = () => {
    if (!isMicrosoftConfigured()) {
      toast.error('Microsoft integration not configured. Check environment variables.')
      return
    }

    const redirectUri = `${window.location.origin}/dashboard/settings`
    const authUrl = getAuthUrl(redirectUri)

    if (authUrl) {
      window.location.href = authUrl
    }
  }

  const disconnectMicrosoft = async () => {
    setLoading(true)
    const client = getSupabase()

    try {
      // Delete tokens
      await client.from('microsoft_tokens').delete().eq('user_id', user.id)

      // Update profile
      await client.from('profiles').update({
        microsoft_connected: false,
        microsoft_email: null,
        calendar_sync_enabled: false,
        todo_sync_enabled: false,
      }).eq('id', user.id)

      setMicrosoftConnected(false)
      setMicrosoftEmail(null)
      setCalendarSyncEnabled(false)
      setTodoSyncEnabled(false)

      toast.success('Microsoft 365 disconnected')

    } catch (err) {
      console.error('Disconnect error:', err)
      toast.error('Failed to disconnect')
    }

    setLoading(false)
  }

  const updateSyncSettings = async (setting, value) => {
    const client = getSupabase()

    try {
      await client.from('profiles').update({
        [setting]: value,
      }).eq('id', user.id)

      if (setting === 'calendar_sync_enabled') setCalendarSyncEnabled(value)
      if (setting === 'todo_sync_enabled') setTodoSyncEnabled(value)

      toast.success('Settings updated')
    } catch (err) {
      toast.error('Failed to update settings')
    }
  }

  const microsoftConfigured = isMicrosoftConfigured()

  return (
    <DashboardLayout title="Settings">
      {/* Microsoft 365 Integration */}
      <Card className="mb-6">
        <h3 className="card-title mb-4">Microsoft 365 Integration</h3>

        {!microsoftConfigured ? (
          <div className="text-muted">
            <p className="mb-2">Microsoft 365 integration is not configured.</p>
            <p className="text-sm">
              To enable, add <code>VITE_MICROSOFT_CLIENT_ID</code> to your environment variables.
              See <code>src/lib/microsoft.js</code> for setup instructions.
            </p>
          </div>
        ) : microsoftConnected ? (
          <>
            <div className="flex items-center gap-3 mb-4 p-3 rounded" style={{ background: 'var(--success-light)' }}>
              <span style={{ fontSize: '1.5rem' }}>✓</span>
              <div>
                <p className="font-medium" style={{ color: 'var(--success)' }}>Connected</p>
                {microsoftEmail && <p className="text-sm text-muted">{microsoftEmail}</p>}
              </div>
            </div>

            {/* Sync Options */}
            <div className="mb-4">
              <h4 className="font-medium mb-3">Sync Options</h4>

              <label className="flex items-center gap-3 p-3 border rounded mb-2" style={{ borderColor: 'var(--gray-200)' }}>
                <input
                  type="checkbox"
                  checked={calendarSyncEnabled}
                  onChange={(e) => updateSyncSettings('calendar_sync_enabled', e.target.checked)}
                />
                <div>
                  <p className="font-medium">Calendar Sync</p>
                  <p className="text-sm text-muted">Sync task due dates to Outlook Calendar</p>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 border rounded" style={{ borderColor: 'var(--gray-200)' }}>
                <input
                  type="checkbox"
                  checked={todoSyncEnabled}
                  onChange={(e) => updateSyncSettings('todo_sync_enabled', e.target.checked)}
                />
                <div>
                  <p className="font-medium">Microsoft To Do Sync</p>
                  <p className="text-sm text-muted">Sync tasks to Microsoft To Do app</p>
                </div>
              </label>
            </div>

            <Button
              variant="danger"
              onClick={disconnectMicrosoft}
              loading={loading}
            >
              Disconnect Microsoft 365
            </Button>
          </>
        ) : (
          <>
            <p className="text-muted mb-4">
              Connect your Microsoft 365 account to:
            </p>
            <ul className="text-muted text-sm mb-4" style={{ paddingLeft: '1.25rem' }}>
              <li>Sync task due dates to your Outlook Calendar</li>
              <li>Send invoice emails through your business email</li>
              <li>Sync tasks to Microsoft To Do</li>
            </ul>
            <Button
              variant="primary"
              onClick={connectMicrosoft}
              loading={loading}
            >
              Connect Microsoft 365
            </Button>
          </>
        )}
      </Card>

      {/* Account Settings */}
      <Card className="mb-6">
        <h3 className="card-title mb-4">Account</h3>
        <div className="flex flex-col gap-2">
          <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--gray-100)' }}>
            <span className="text-muted">Email</span>
            <span>{user?.email}</span>
          </div>
          <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--gray-100)' }}>
            <span className="text-muted">Name</span>
            <span>{profile?.name}</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-muted">Role</span>
            <span className="capitalize">{profile?.role}</span>
          </div>
        </div>
      </Card>

      {/* Danger Zone */}
      <Card>
        <h3 className="card-title mb-4" style={{ color: 'var(--error)' }}>Danger Zone</h3>
        <p className="text-muted text-sm mb-4">
          These actions are irreversible. Please be careful.
        </p>
        <Button variant="danger" disabled>
          Delete Account
        </Button>
        <p className="text-sm text-muted mt-2">
          Contact an admin to delete your account.
        </p>
      </Card>
    </DashboardLayout>
  )
}

export default SettingsPage
